import { useNavigate } from '@tanstack/react-router';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAction } from '~/api/hooks';
import { UploadIcon } from '~/ui/icons';
import { Meter } from '~/ui/Meter';
import { useToast } from '~/ui/Toast';
import { paths } from './paths';

type DropTarget = { conversationId: string | null; stay: boolean };

const DropContext = createContext<{
  setTarget: (t: DropTarget | null) => void;
  upload: (files: File[]) => void;
  uploading: boolean;
} | null>(null);

/** Screens with a conversation register it, so a drop lands in that conversation. */
export function useDropTarget(conversationId: string | null | undefined, stay = true) {
  const ctx = useContext(DropContext);
  // Keyed on the stable setter, not the context value: registering a target changes the zone's
  // state, and an effect keyed on the whole value would register again, forever.
  const setTarget = ctx?.setTarget;
  useEffect(() => {
    if (!setTarget || conversationId === undefined) return;
    setTarget({ conversationId, stay });
    return () => setTarget(null);
  }, [setTarget, conversationId, stay]);
  return ctx ?? { upload: () => {}, uploading: false, setTarget: () => {} };
}

export function useUpload() {
  const ctx = useContext(DropContext);
  return ctx ?? { upload: () => {}, uploading: false, setTarget: () => {} };
}

/** While files travel to the api: how many, and how far along. Masking starts when they land. */
export function UploadProgress({ count, pct }: { count: number; pct: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-5 left-1/2 z-40 w-[min(420px,calc(100vw-32px))] -translate-x-1/2"
    >
      <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface px-4 py-3 shadow-card">
        <span className="flex justify-between text-14">
          <strong>{count === 1 ? 'Uploading 1 document' : `Uploading ${count} documents`}</strong>
          <span className="text-muted">{pct}%</span>
        </span>
        <Meter pct={pct} height={4} label={`Uploaded ${pct} percent`} />
        <span className="text-12 text-muted">
          Masking starts as soon as they arrive. No client needed.
        </span>
      </div>
    </div>
  );
}

/** The overlay that appears while files are dragged over the window. */
export function DropOverlay({ count }: { count: number | null }) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-[var(--aw-scrim)] p-10"
      aria-hidden="true"
    >
      <div className="box-border flex w-full max-w-[640px] flex-col items-center gap-3 rounded-2xl border-[3px] border-dashed border-[var(--aw-drop-line-strong)] bg-white/95 px-8 py-12 text-center">
        <UploadIcon size={40} weight={1.6} className="text-blue" />
        <strong className="text-24 text-ink">
          {count && count > 1 ? `Drop to add ${count} documents` : 'Drop to add a document'}
        </strong>
        <span className="text-muted">
          No client needed. Identifiers are masked before anything is read.
        </span>
      </div>
    </div>
  );
}

/**
 * Drop anywhere: a document dropped on any screen goes to intake in that screen's conversation,
 * or the firm conversation when the screen has none. No client needed.
 */
export function DropZone({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<DropTarget | null>(null);
  // read at drop time, so the upload callback (and the context) stays the same as targets change
  const targetRef = useRef<DropTarget | null>(null);
  targetRef.current = target;
  const [dragging, setDragging] = useState<number | null>(null);
  const depth = useRef(0);
  const navigate = useNavigate();
  const toast = useToast();
  const [sending, setSending] = useState<{ count: number; pct: number } | null>(null);
  const action = useAction((api, files: File[]) =>
    api.uploadDocuments(files, {
      conversationId: targetRef.current?.conversationId ?? null,
      onProgress: (pct) => setSending({ count: files.length, pct }),
    }),
  );

  const upload = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setSending({ count: files.length, pct: 0 });
      action.mutate(files, {
        onSettled: () => setSending(null),
        onSuccess: (docs) => {
          toast.show(
            docs.length === 1
              ? 'Added 1 document. Masking it now.'
              : `Added ${docs.length} documents. Masking them now.`,
            'success',
          );
          if (!targetRef.current?.stay) void navigate({ to: paths.intake });
        },
      });
    },
    [action, navigate, toast],
  );

  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(e.dataTransfer?.items?.length ?? null);
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const leave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(null);
    };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(null);
      upload(Array.from(e.dataTransfer?.files ?? []));
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  }, [upload]);

  const value = useMemo(
    () => ({ setTarget, upload, uploading: action.isPending }),
    [upload, action.isPending],
  );
  return (
    <DropContext.Provider value={value}>
      {children}
      {dragging !== null && <DropOverlay count={dragging} />}
      {sending && <UploadProgress count={sending.count} pct={sending.pct} />}
    </DropContext.Provider>
  );
}
